import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { from, of } from 'rxjs';
import { withLatestFrom, switchMap, map, catchError, tap } from 'rxjs/operators';
import * as SessionActions from '../actions/session.actions';
import * as UserSelectors from '../selectors/user.selectors';
import { AppState } from '../states/app.state';
import { BoardsService } from '../../services/boards.service';
import { Board, BoardHeader, BoardStage, Tag } from '../../entities/board.entity';
import { WebArticle } from '../../entities/web-article';
import { Router } from '@angular/router';
import { selectAllTags } from '../selectors/session.selectors';
import { ErrorToastService } from '../../services/error-toast.service';
import { environment } from '../../../../environments/environment';


@Injectable()
export class SessionEffects {

  loadDraftBoards$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.loadDraftBoards),
    withLatestFrom(this.store.pipe(select(UserSelectors.selectCurrentUserSub))),
    switchMap(([action, ownerUUID]) => {
      if (!environment.boardsEnabled) {
        return of(SessionActions.setDraftBoards({ draftBoards: [] }));
      }
      if (!ownerUUID) {
        return of(SessionActions.loadDraftBoardsFailure({ error: 'No ownerUUID found' }));
      }
      return from(this.boardService.getBoards(ownerUUID, BoardStage.DRAFT, null)).pipe(
        map(data => {
          const boards: BoardHeader[] = data.items.map((d: Board) => {
            return {
              boardUUID: d.boardUUID,
              title: d.title,
              stage: d.stage,
              ownerUUID: d.ownerUUID,
              members: d.members,
              tags: d.tags,
              createDate: d.createDate,
              articleCount: d.articles?.length || 0,
              articleRef: d.articles?.length ? d.articles.map((article: WebArticle) => {
                return article.documentNumber ?? null;
              }): [],
              calendarActivitiesCount: d.calendarActivities?.length || 0,
              calendarActivitiesRef: d.calendarActivities?.length ? d.calendarActivities.map((activity: any) => {
                return activity.activityID ?? null;
              }): []
            };
          });
          return SessionActions.setDraftBoards({ draftBoards: boards });
        }),
        catchError(error => {
          this.errorToast.showErrorWithDetails('An Error Occurs!', error.message);
          return of(SessionActions.loadDraftBoardsFailure({ error: error.toString() }));
        })
      );
    })
  ));

  addUniqueTags$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.addTags),
    withLatestFrom(this.store.pipe(select(selectAllTags))),
    map(([action, existingTags]) => {
      const allTags = [...existingTags, ...action.tags];
      const uniqueTags = Array.from(new Set(allTags.map(tag => tag.uuid)))
        .map(uuid => allTags.find(tag => tag.uuid === uuid))
        .filter((tag): tag is Tag => tag !== undefined);
      return SessionActions.setTags({ tags: uniqueTags });
    }),
    catchError(error => {
      this.errorToast.showErrorWithDetails('An Error Occurs!', error.message);
      return of();
    })
  ));


  deleteTag$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.deleteTag),
    withLatestFrom(this.store.pipe(select(selectAllTags))),
    map(([action, existingTags]) => {
      const updatedTags = existingTags.filter(tag => tag.uuid !== action.uuid);
      return SessionActions.setTags({ tags: updatedTags });
    }),
    catchError(error => {
      this.errorToast.showErrorWithDetails('An Error Occurs!', error.message);
      return of();
    })
  ));



  navigateAfterSelect$ = createEffect(() =>
  this.actions$.pipe(
    ofType(SessionActions.setSelectedBoard),
    tap(({ selectedBoard }) => {
      if (selectedBoard && environment.boardsEnabled) {
        localStorage.setItem('selectedBoard', JSON.stringify(selectedBoard));
        this.router.navigate(['/boards/workarea']);
      }
    })
  ),
  { dispatch: false }
);

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private boardService: BoardsService,
    private errorToast: ErrorToastService,
    private router: Router
  ) { }
}
